import {
  hasSpecialCharacters,
  isBRServer,
  maxDate,
  minDate,
  required,
  validateCep,
  validateCompanyName,
  validateEmail,
  validatePersonName,
  validatePhoneBR,
} from '@/utils/Validators/validation';
import {
  validateDocument as validateDocumentBase,
} from '@/utils/Validators/cpfCnpjValidation';
import { COUNTRY_DOCUMENT_RULES } from '../FastCheckout.enum';
import { format, parse } from 'date-fns';
import { COUNTRY } from '@/config';

/**
 * Validates a document based on country and foreigner status
 * @param {string} document - The document to validate
 * @param {boolean} isForeigner - Whether the user is foreigner
 * @param {function} setIsCompany - Function to set the company state
 * @param {boolean} enableNewValidationCnpjAlphanumeric - Feature flag for alphanumeric CNPJ
 * @returns {string|boolean} Error code or false if valid
 */
export const validateDocument = (document, isForeigner, setIsCompany, enableNewValidationCnpjAlphanumeric = false) => {
  // Usa a validação centralizada para BR
  const baseValidation = validateDocumentBase(document, isForeigner, setIsCompany, enableNewValidationCnpjAlphanumeric);

  if (isBRServer) {
    return baseValidation;
  }

  // Lógica adicional para outros países
  const countryDocumentRule = COUNTRY_DOCUMENT_RULES[COUNTRY];
  if (countryDocumentRule) {
    if (document.length < countryDocumentRule.min || document.length > countryDocumentRule.max) {
      return 'invalidDocument';
    }
    return false;
  }

  return baseValidation;
};

/**
 * Validates a date in dd/MM/yyyy format
 * @param {string} date - The date to validate
 * @returns {boolean} True if invalid, false if valid
 */
export const validateDate = (date) => {
  const isDate = parse(date, 'dd/MM/yyyy', new Date());
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(isDate)) {
    return true;
  }

  const birthDateMin = minDate(date, '01/01/1900');
  const birthDateMax = maxDate(date, format(new Date(), 'dd/MM/yyyy'));

  return !(birthDateMin && birthDateMax);
};

/**
 * Validates a phone number based on country
 * @param {string} phoneNumber - The phone number to validate
 * @param {Object} summaryState - The summary state containing country information
 * @returns {boolean} True if invalid, false if valid
 */
export const validatePhone = (phoneNumber, summaryState) => {
  if (summaryState.phoneNumberCountry === 'BR') {
    return !validatePhoneBR(phoneNumber);
  }
  return false;
};

/**
 * Validates a postal code based on brand
 * @param {string} postalCode - The postal code to validate
 * @param {string} brand - The brand code (country)
 * @returns {string|boolean} Error code or false if valid
 */
export const validatePostalCode = (postalCode, brand) => {
  if (brand === 'BR') {
    return !validateCep(postalCode);
  }
  return !required(postalCode) ? 'required' : false;
};

/**
 * Validates a complement for special characters
 * @param {string} complement - The complement to validate
 * @returns {boolean} True if invalid, false if valid
 */
export const validateComplement = complement => isBRServer && hasSpecialCharacters(complement);

/**
 * Creates form validation rules object with context
 * @param {Object} context - The validation context
 * @returns {Object} Validation rules object
 */
export const createFormValidationRules = ({
  brand,
  isForeigner,
  summaryState,
  setIsCompany,
  enableNewValidationCnpjAlphanumeric = false,
}) => ({
  birthDate: validateDate,
  cep: validateCep,
  document: document => validateDocument(document, isForeigner, setIsCompany, enableNewValidationCnpjAlphanumeric),
  email: value => !validateEmail(value),
  name: value => !validatePersonName(value),
  companyName: value => !validateCompanyName(value),
  phone: phoneNumber => validatePhone(phoneNumber, summaryState),
  postalCode: postalCode => validatePostalCode(postalCode, brand),
  numberAndComplement: validateComplement,
  required: value => (!required(value) ? 'required' : false),
});

/**
 * Main validation function that routes to the appropriate validator
 * @param {Object} options - Validation options
 * @returns {function} Validation function
 */
export const createValidator = (options) => {
  const validationRules = createFormValidationRules(options);

  return (fieldName, fieldValue) => {
    if (!required(fieldValue)) {
      return 'required';
    }

    return validationRules[fieldName]
      ? validationRules[fieldName](fieldValue)
      : validationRules.required(fieldValue);
  };
};
